"use client";

import React, { Component, ReactNode } from "react";
import { ErrorLogService } from "@/services/errorLog";
import { useI18n } from "@/i18n/I18nProvider";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  componentName?: string;
  t?: (key: string, fallback?: string) => string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary component to catch React errors and log them
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary componentName="MyComponent">
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 */
class ErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to backend
    ErrorLogService.logComponentError(
      this.props.componentName || "Unknown Component",
      error,
      {
        componentStack: errorInfo.componentStack || undefined,
      }
    );

    // Also log to console in development
    if (process.env.NODE_ENV === "development") {
      console.error("ErrorBoundary caught an error:", error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900 text-center">
              {this.props.t?.("error.boundary.title") || "Algo deu errado"}
            </h3>
            <p className="mt-2 text-sm text-gray-500 text-center">
              {this.props.t?.("error.boundary.message") ||
                "Desculpe, ocorreu um erro inesperado. O problema foi registrado e será investigado."}
            </p>
            <div className="mt-6">
              <button
                onClick={() => window.location.reload()}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                {this.props.t?.("error.boundary.reload") || "Recarregar página"}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Wrapper component that provides i18n context to ErrorBoundary
 */
export function ErrorBoundary({
  children,
  fallback,
  componentName,
}: {
  children: ReactNode;
  fallback?: ReactNode;
  componentName?: string;
}) {
  const { t } = useI18n();

  return (
    <ErrorBoundaryClass componentName={componentName} fallback={fallback} t={t}>
      {children}
    </ErrorBoundaryClass>
  );
}
